'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { VIMEO_EMBED_TEMPLATE, VIMEO_REPLACE_PATTERN } from '@workspace/constants';
import { cn } from '@workspace/lib';
import { SanityImage } from '@workspace/interfaces';
import { AppImage } from '@workspace/ui/common';

// Small helper to safely resolve a Sanity file/image URL or a Bynder asset URL.
const getAssetUrl = (asset?: (SanityImage & Record<string, any>) | null): string =>
  asset?.videoUrl || asset?.selectedUrl || asset?.url || asset?.asset?.url || '';

type Size = { width: number; height: number };

interface HeroBannerVideoProps {
  videoUrl: string;
  videoFile?: SanityImage | null;
  bynderVideo?: any;
  poster?: SanityImage | null;
  isActive?: boolean | null;
  shouldPreload?: boolean;
}

export function HeroBannerVideo({
  videoUrl,
  videoFile,
  bynderVideo,
  poster,
  isActive = false,
  shouldPreload = false,
}: HeroBannerVideoProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [parentSize, setParentSize] = useState<Size | null>(null);

  // mark client mount to avoid SSR hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // memoize embed url and params once
  const embedUrl = useMemo(() => {
    if (!videoUrl) return '';
    const base = videoUrl.includes('vimeo.com')
      ? videoUrl.replace(VIMEO_REPLACE_PATTERN, VIMEO_EMBED_TEMPLATE)
      : videoUrl;
    const params = 'autoplay=1&loop=1&muted=1&background=1&controls=0&title=0&byline=0&portrait=0';
    // avoid accidental trailing '?'
    const joiner = base.includes('?') ? '&' : '?';
    return `${base}${joiner}${params}`;
  }, [videoUrl]);

  const videoFileUrl = useMemo(() => getAssetUrl(videoFile), [videoFile]);
  const bynderVideoUrl = useMemo(() => getAssetUrl(bynderVideo), [bynderVideo]);
  // Preference order: videoFile > bynderVideo > Vimeo (iframe)
  const resolvedFileUrl = videoFileUrl || bynderVideoUrl;
  const posterUrl = useMemo(() => getAssetUrl(poster), [poster]);
  const posterAlt = poster?.bynderImage?.alt || poster?.alt || 'Hero banner poster';
  const shouldEagerLoad = shouldPreload || !!isActive;

  // ResizeObserver on the parent of container (so iframe covers parent's box)
  useEffect(() => {
    if (!isMounted) return;
    const container = containerRef.current;
    const parent = container?.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver((entries) => {
      // Only one element is observed
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setParentSize((prev) => {
        const w = Math.max(cr.width, 0);
        const h = Math.max(cr.height, 0);
        if (prev && prev.width === w && prev.height === h) return prev;
        return { width: w, height: h };
      });
    });

    // initialize
    const rect = parent.getBoundingClientRect();
    setParentSize({ width: rect.width, height: rect.height });

    ro.observe(parent);
    return () => ro.disconnect();
  }, [isMounted]);

  // IntersectionObserver: watch the iframe itself
  useEffect(() => {
    if (!isMounted) return;
    const iframe = iframeRef.current;
    if (!iframe || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsVisible(Boolean(entry?.isIntersecting));
      },
      {
        threshold: 0.1,
        rootMargin: '10px',
      },
    );

    observer.observe(iframe);
    return () => observer.disconnect();
  }, [isMounted]);

  // Control video playback through postMessage (Vimeo embeds)
  useEffect(() => {
    if (!isMounted) return;
    const iframe = iframeRef.current;
    const target = iframe?.contentWindow;
    if (!iframe || !target) return;

    const shouldPlay = isVisible && isActive;

    let playTimer: number | undefined;
    try {
      if (shouldPlay) {
        target.postMessage({ method: 'setCurrentTime', value: 0 }, '*');
        // let Vimeo apply the time, then play
        playTimer = window.setTimeout(() => {
          iframe.contentWindow?.postMessage({ method: 'play' }, '*');
        }, 100);
      } else {
        target.postMessage({ method: 'pause' }, '*');
      }
    } catch (err) {
      console.warn('Could not control video playback:', err);
    }

    return () => {
      if (playTimer) window.clearTimeout(playTimer);
    };
  }, [isMounted, isVisible, isActive]);

  // compute 16:9 cover size to fill parent while preserving aspect
  const coverSize = useMemo<Size | null>(() => {
    if (!parentSize) return null;
    const R = 16 / 9;
    const parentW = Math.max(parentSize.width, 0);
    const parentH = Math.max(parentSize.height, 0);
    if (!parentW || !parentH) return null;

    let width = parentW;
    let height = Math.round(width / R);
    if (height < parentH) {
      height = parentH;
      width = Math.round(height * R);
    }
    return { width, height };
  }, [parentSize]);

  if (!videoUrl && !resolvedFileUrl) return null;

  // SSR placeholder to avoid layout shift/hydration mismatch
  if (!isMounted) {
    return (
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-black">
        <div className="text-center text-white" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden bg-black')}
      style={{
        width: parentSize?.width,
        height: parentSize?.height,
      }}
    >
      {posterUrl ? (
        <div className="absolute inset-0 -z-10">
          <AppImage
            src={posterUrl}
            alt={posterAlt}
            fill
            sanityImage={poster ?? undefined}
            priority={shouldEagerLoad}
            fetchPriority={shouldEagerLoad ? 'high' : undefined}
            className="h-full w-full"
            overlayGradient={{ startColor: 'rgba(0,0,0,0)', endColor: 'rgba(0,0,0,0)', opacity: 0 }}
            aria-hidden
          />
        </div>
      ) : null}

      {resolvedFileUrl ? (
        <div
          className={cn(
            'absolute inset-0 z-0 pointer-events-none',
            'h-full w-full',
            'overflow-hidden',
            'bg-cover bg-no-repeat bg-center',
          )}
        >
          {shouldEagerLoad && <link rel="preload" as="video" href={resolvedFileUrl} fetchPriority="high" />}
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={posterUrl || undefined}
            preload={shouldEagerLoad ? 'auto' : 'metadata'}
            className={cn(
              'absolute top-1/2 left-1/2',
              'h-full w-full',
              'object-cover',
              '-translate-x-1/2 -translate-y-1/2',
            )}
          >
            <source src={resolvedFileUrl} type="video/mp4" />
          </video>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={embedUrl}
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: coverSize?.width,
            height: coverSize?.height,
          }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading={shouldEagerLoad ? 'eager' : 'lazy'}
          title={`Hero banner video - ${videoUrl}`}
        />
      )}
    </div>
  );
}
